import { useEffect, useRef, useState, type ReactNode } from 'react';

import { Alert } from '@clay-run/glaze/alert';
import { Badge } from '@clay-run/glaze/badge';
import { Chip } from '@clay-run/glaze/chip';
import { ComboboxMultipleOption } from '@clay-run/glaze/combobox-multiple';
import { ComboboxMultipleField } from '@clay-run/glaze/combobox-multiple-field';
import { InputField } from '@clay-run/glaze/input-field';
import { Text } from '@clay-run/glaze/text';
import { TextareaField } from '@clay-run/glaze/textarea-field';

import { isThinDescription } from '../copy-generation-rules';
import type { GenerateWithAIForm } from '../generate-with-ai-store';
import type { PlaybooksGenerationContextResponse } from '../schemas';
import { useAutosizeTextarea } from '../use-autosize-textarea';

/**
 * Launcher V2 U7b (terra-l2ai-epic-076.12) — `GenerateWithAIDrawer`'s stage-1
 * fields, split into their own file per the bead's "Day-One Test" note: this
 * is the piece U9 (library "Generate with AI" entry) reuses UNCHANGED, with
 * only a `campaignPicker` slot swapped in where this component's embedded
 * caller passes a fixed read-only `campaign` header instead.
 *
 * Every field below is a CONTROLLED reflection of `form`/`onChange` — this
 * component seeds nothing itself. `GenerateWithAIDrawer` owns the "seed once
 * per campaign" effect (bead's own explicit rule: `updateGenerateForm` only
 * when `form.campaignSfdcId` changes, never on every render) and hands the
 * resolved values down as plain `form` fields, exactly like every other
 * controlled Glaze form in this app.
 */

/** The embedded surface's fixed campaign header — resolved by `SequencesStep.tsx` from its own `Campaign`/`campaignSfdcId` props. */
export interface GenerateWithAIEmbeddedCampaign {
  sfdcId: string;
  name: string;
  type: string | null;
  /** Pre-formatted display string (e.g. `"Sep 12 – Sep 14"`), or `null` when the campaign has no dates. */
  dates: string | null;
}

export interface GenerateConfigureFormProps {
  /** Non-`null` on the embedded surface (a rung already has a campaign) — renders a fixed read-only header. `null` on the library surface until a campaign is chosen, which renders {@link campaignPicker} instead. */
  campaign: GenerateWithAIEmbeddedCampaign | null;
  /** U9's library campaign picker — rendered only when {@link campaign} is `null`. Never used on the embedded surface. */
  campaignPicker?: ReactNode;
  form: GenerateWithAIForm;
  onChange: (patch: Partial<GenerateWithAIForm>) => void;
  /** `GET .../generation-context`'s response, once resolved — `null` while loading or before a campaign is chosen. */
  context: PlaybooksGenerationContextResponse | null;
  /**
   * D11 — whether the `generation-context` query has SETTLED (succeeded or
   * failed) for the currently-chosen campaign. Fields below the campaign
   * header stay disabled until this is `true` — so a rep can't type into a
   * field the context-seed effect is about to overwrite the instant it
   * lands. `true` when there is no campaign chosen yet is fine (the OTHER
   * half of `fieldsDisabled` already covers that case); the caller
   * (`GenerateWithAIDrawer`) only needs to pass a real answer once a
   * campaign is actually selected.
   */
  contextSettled: boolean;
  /** D11 — the failed query's rep-facing message, shown as an inline error banner once `contextSettled` is `true` with no `context`. `null`/omitted otherwise. */
  contextError?: string | null;
}

export function GenerateConfigureForm({
  campaign,
  campaignPicker,
  form,
  onChange,
  context,
  contextSettled,
  contextError = null,
}: GenerateConfigureFormProps) {
  // Everything below the campaign header is disabled until a campaign is
  // resolved AND its context fetch has settled (D11) — matching the bead's
  // own approach text ("everything below disabled until form.campaignSfdcId"),
  // extended so typing can't race the context-seed effect that fires the
  // instant the fetch resolves.
  const fieldsDisabled = form.campaignSfdcId === null || !contextSettled;
  // G1 (round 4) — autosize to content, exactly like `SequenceStepEditor.tsx`'s
  // Body field (`:250-257`, `:373-388`): a ref per field feeding
  // `useAutosizeTextarea`. No remount key — none of these three textareas
  // sit inside a `CollapsibleCard` (this form renders in a flat `PushDrawer`),
  // so there is no unmount/remount to detect.
  const descriptionRef = useRef<HTMLTextAreaElement>(null);
  const instructionRef = useRef<HTMLTextAreaElement>(null);
  const sourceMaterialRef = useRef<HTMLTextAreaElement>(null);
  useAutosizeTextarea(descriptionRef, form.campaignDescription);
  useAutosizeTextarea(instructionRef, form.instruction);
  useAutosizeTextarea(sourceMaterialRef, form.sourceMaterial);
  const facts = context?.campaign ?? null;
  const statuses = context?.rosterStatusCounts ?? [];
  const countByStatus = new Map(statuses.map((s) => [s.status, s.count]));
  const statusMixDescription =
    statuses.length > 0 ? statuses.map((s) => `${s.status} (${s.count})`).join(', ') : undefined;

  // Campaign member status search (2026-09-11 QA round 2, bug 3): mirrors
  // `GenerateCampaignPicker`'s own `inputValue`/`onInputValueChange` pattern —
  // `ComboboxMultipleField` does no filtering on its own (Glaze's own doc:
  // "callers render a pre-filtered items array themselves"), so without this
  // wiring typing into the field did nothing.
  const [statusQuery, setStatusQuery] = useState('');
  useEffect(() => {
    setStatusQuery('');
  }, [form.campaignSfdcId]);
  const trimmedStatusQuery = statusQuery.trim().toLowerCase();
  const filteredStatuses =
    trimmedStatusQuery === ''
      ? statuses
      : statuses.filter((s) => s.status.toLowerCase().includes(trimmedStatusQuery));

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1.5">
        <Text size="xs" weight="medium" variant="tertiary">
          Campaign
        </Text>
        {campaign ? (
          <div className="flex flex-wrap items-center gap-1.5">
            <Text size="sm" weight="semibold">
              {campaign.name}
            </Text>
            {campaign.type && <Badge>{campaign.type}</Badge>}
            {campaign.dates && (
              <Text size="xs" variant="tertiary">
                {campaign.dates}
              </Text>
            )}
          </div>
        ) : (
          campaignPicker ?? null
        )}
      </div>

      {/* D11 — settled-with-error: fields enable (below) with empty seeds; this banner is the rep's only signal something went wrong. */}
      {contextSettled && context === null && contextError && <Alert status="error">{contextError}</Alert>}

      <InputField
        label="Event name as it reads in copy"
        value={form.eventName}
        onChange={(e) => onChange({ eventName: e.target.value })}
        disabled={fieldsDisabled}
      />

      <div className="flex flex-col gap-2">
        <TextareaField
          ref={descriptionRef}
          label="Description"
          value={form.campaignDescription}
          onValueChange={(campaignDescription) => onChange({ campaignDescription })}
          disabled={fieldsDisabled}
          resizable={false}
          // `className` on `TextareaField`/`Textarea` lands on the OUTER
          // wrapper `<span>`, not the `<textarea>` itself — `style` flows
          // straight through to the raw element, so the min-height guard has
          // to go here rather than as a Tailwind class (see
          // `SequenceStepEditor.tsx:379-386`).
          style={{ minHeight: '4.5rem' }}
        />
        {facts && (
          <div className="flex flex-wrap gap-1.5">
            {facts.subtype && <Chip>{facts.subtype}</Chip>}
            {facts.startDate && <Chip>Starts {facts.startDate}</Chip>}
            {facts.endDate && <Chip>Ends {facts.endDate}</Chip>}
            {facts.city && <Chip>{facts.city}</Chip>}
            {facts.state && <Chip>{facts.state}</Chip>}
            {facts.targetPersona && <Chip>{facts.targetPersona}</Chip>}
            {facts.goal && <Chip>{facts.goal}</Chip>}
            {facts.eventBriefUrl && <Chip>Event brief</Chip>}
          </div>
        )}
        {!fieldsDisabled && isThinDescription(form.campaignDescription) && (
          <Alert status="warning">
            Generated copy may invent details — add context or paste source material below.
          </Alert>
        )}
      </div>

      <ComboboxMultipleField<string>
        label="Campaign member status (any of)"
        description={statusMixDescription}
        items={filteredStatuses.map((s) => s.status)}
        value={form.memberStatuses}
        onValueChange={(memberStatuses) => onChange({ memberStatuses })}
        itemToStringLabel={(status) => status}
        inputValue={statusQuery}
        onInputValueChange={setStatusQuery}
        disabled={fieldsDisabled}
      >
        {filteredStatuses.map((s) => (
          <ComboboxMultipleOption key={s.status} value={s.status}>
            {`${s.status} · ${countByStatus.get(s.status) ?? s.count}`}
          </ComboboxMultipleOption>
        ))}
      </ComboboxMultipleField>

      <TextareaField
        ref={instructionRef}
        label="Instruction"
        value={form.instruction}
        onValueChange={(instruction) => onChange({ instruction })}
        disabled={fieldsDisabled}
        resizable={false}
        style={{ minHeight: '4.5rem' }}
      />

      <TextareaField
        ref={sourceMaterialRef}
        label="Source material (optional)"
        value={form.sourceMaterial}
        onValueChange={(sourceMaterial) => onChange({ sourceMaterial })}
        disabled={fieldsDisabled}
        resizable={false}
        style={{ minHeight: '4.5rem' }}
      />
    </div>
  );
}
